import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { submitSurvey } from "../utils/api";

/* ───── helpers ───── */
const severity = ["None","Mild","Moderate","Severe","Very Severe"];
const frequency = ["Never","Rarely (< 1/wk)","Sometimes (1-3/wk)","Often (4-6/wk)","Always (daily)"];
const SECTION_COUNT = 9;

function today() {
  const d = new Date();
  return d.toISOString().slice(0, 16);          // for datetime-local
}

/* ───── tiny reusable field components ───── */
function Field({ label, required, children }) {
  return (
    <div className="survey-field">
      <label>{label}{required && <span className="req">*</span>}</label>
      {children}
    </div>
  );
}
function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="radio-group">
      {options.map(o => (
        <label key={o} className={`radio-pill${value === o ? " active" : ""}`}>
          <input type="radio" name={name} value={o} checked={value === o} onChange={() => onChange(o)} />
          {o}
        </label>
      ))}
    </div>
  );
}
function CheckboxGroup({ options, value, onChange }) {
  const toggle = (o) => {
    const next = value.includes(o) ? value.filter(v => v !== o) : [...value, o];
    onChange(next);
  };
  return (
    <div className="checkbox-group">
      {options.map(o => (
        <label key={o} className={`check-pill${value.includes(o) ? " active" : ""}`}>
          <input type="checkbox" checked={value.includes(o)} onChange={() => toggle(o)} />
          {o}
        </label>
      ))}
    </div>
  );
}
function ScaleRow({ label, sevValue, freqValue, onSev, onFreq }) {
  return (
    <div className="symptom-row">
      <div className="symptom-label">{label}</div>
      <div className="symptom-scales">
        <div className="scale-col">
          <span className="scale-heading-inline">Severity</span>
          <div className="scale-buttons">
            {severity.map((s, i) => (
              <button type="button" key={i} className={`scale-btn${sevValue === i ? " active sev" : ""}`} onClick={() => onSev(i)} title={s}>{i}</button>
            ))}
          </div>
        </div>
        <div className="scale-col">
          <span className="scale-heading-inline">Frequency</span>
          <div className="scale-buttons">
            {frequency.map((f, i) => (
              <button type="button" key={i} className={`scale-btn${freqValue === i ? " active freq" : ""}`} onClick={() => onFreq(i)} title={f}>{i}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───── initial state factory ───── */
function initState() {
  return {
    /* S1 Visit */
    visitType: "", visitDateTime: today(), participantId: "", fasting: "", hoursFasted: "",
    hoursSinceExercise: "", infectionPast30: "", infPathogen: "", infDate: "", infCare: "",
    vaccinePast90: "", vacType: "", vacDate: "", menstrualStatus: "", cycleDay: "",
    /* S2 Demographics */
    birthYear: "", sexAtBirth: "", genderIdentity: "", raceEthnicity: [],
    country: "", state: "", postalCode: "", maritalStatus: "",
    childrenHome: "", childrenCount: "", education: "", employment: "",
    income: "", militaryService: "", tobaccoUse: "",
    /* S3 Medical History */
    illnessOnset: "", triggers: [], diagnoses: [],
    mostImpactful: "", covidCount: "", covidDates: "", covidSeverity: "", covidTreatment: "",
    erVisits12mo: "", erReasons: "", familyHistory: "", familyDesc: "", allergies: "",
    /* S4 QoL */
    overallHealth: "", healthChange: "", avgPain7d: "", worstPain30d: "",
    fatigueSeverity: "", pem: "", pemOnset: "", pemDuration: "",
    sleepHours: "", unrefreshingSleep: "", insomnia: "",
    anxiety: "", lowMood: "", socialLimit: "", workImpact: "",
    /* S5 Lifestyle */
    alcohol: "", caffeine: "", dietPatterns: [], supplements: "", supplementList: "",
    lightMin: "", moderateMin: "", vigorousMin: "", pacing: "",
    bedtime: "", wakeTime: "", napsPerWeek: "", perceivedStress: "",
    moldExposure: "", chemSensitivity: "", petsHome: "", travelPast3mo: "", altitudeExposure: "",
    /* S6 Symptoms (each has _sev and _freq) */
    sym_dizziness_sev: -1, sym_dizziness_freq: -1,
    sym_palpitations_sev: -1, sym_palpitations_freq: -1,
    sym_chest_sev: -1, sym_chest_freq: -1,
    sym_sob_sev: -1, sym_sob_freq: -1,
    sym_tempReg_sev: -1, sym_tempReg_freq: -1,
    sym_sweating_sev: -1, sym_sweating_freq: -1,
    sym_brainFog_sev: -1, sym_brainFog_freq: -1,
    sym_memory_sev: -1, sym_memory_freq: -1,
    sym_headache_sev: -1, sym_headache_freq: -1,
    sym_sleepiness_sev: -1, sym_sleepiness_freq: -1,
    sym_insomnia_sev: -1, sym_insomnia_freq: -1,
    sym_lightSens_sev: -1, sym_lightSens_freq: -1,
    sym_tinnitus_sev: -1, sym_tinnitus_freq: -1,
    sym_widePain_sev: -1, sym_widePain_freq: -1,
    sym_jointPain_sev: -1, sym_jointPain_freq: -1,
    sym_instability_sev: -1, sym_instability_freq: -1,
    sym_weakness_sev: -1, sym_weakness_freq: -1,
    sym_stiffness_sev: -1, sym_stiffness_freq: -1,
    sym_spasms_sev: -1, sym_spasms_freq: -1,
    sym_nausea_sev: -1, sym_nausea_freq: -1,
    sym_abPain_sev: -1, sym_abPain_freq: -1,
    sym_bloating_sev: -1, sym_bloating_freq: -1,
    sym_diarrhea_sev: -1, sym_diarrhea_freq: -1,
    sym_constipation_sev: -1, sym_constipation_freq: -1,
    sym_urinary_sev: -1, sym_urinary_freq: -1,
    sym_incomplete_sev: -1, sym_incomplete_freq: -1,
    sym_flushing_sev: -1, sym_flushing_freq: -1,
    sym_hives_sev: -1, sym_hives_freq: -1,
    sym_foodIntol_sev: -1, sym_foodIntol_freq: -1,
    sym_heatPressure_sev: -1, sym_heatPressure_freq: -1,
    sym_dryEyes_sev: -1, sym_dryEyes_freq: -1,
    sym_dental_sev: -1, sym_dental_freq: -1,
    sym_lowMood_sev: -1, sym_lowMood_freq: -1,
    sym_anxietySym_sev: -1, sym_anxietySym_freq: -1,
    sym_irritability_sev: -1, sym_irritability_freq: -1,
    sym_unrefSleep_sev: -1, sym_unrefSleep_freq: -1,
    sym_nightSweats_sev: -1, sym_nightSweats_freq: -1,
    sym_fatiguePersist_sev: -1, sym_fatiguePersist_freq: -1,
    /* S7 Function */
    hoursUpright: "", hoursLying: "", mobilityAids: "",
    workNow: "", daysMissed: "", adlDifficulty: "", iadlDifficulty: "",
    socialParticipation: "", caregiverHours: "",
    /* S8 Disease-Specific */
    lc_infectionDates: "", lc_reinfectionCount: "", lc_reinfectionDates: "",
    lc_vacBeforeFirst: "", lc_currentSymptoms: "", lc_hospCount: "", lc_hospDates: "",
    lc_changeAfterVac: "",
    mecfs_severeFatigue: "", mecfs_infectionOnset: "", mecfs_limitation: "", mecfs_housebound: "",
    pots_hrRise: "", pots_fluidIntake: "", pots_saltUse: "", pots_compression: "",
    pots_compressionHrs: "", pots_uprightIntol: "",
    mcas_triggers: "", mcas_epiCarriage: "", mcas_flushing: "", mcas_anaphylactoid: "",
    sjogren_dryEyes: "", sjogren_dryMouth: "", sjogren_dental: "",
    eds_beighton: "", eds_dislocations: "", eds_painImpact: "", eds_bracesPhysio: "",
    migraine_headacheDays: "", migraine_missedWork: "", migraine_pain: "", migraine_reducedProd: "",
    /* S9 Treatments */
    prescriptions: "", nonDrugInterventions: "", adverseEffects: "",
    erVisits30d: "", erReasons30d: "", barriers: [],
  };
}

/* ───── Main component ───── */
export default function SurveyPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(initState);
  const [section, setSection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // auto-capture date-time on load
  useEffect(() => { setData(d => ({ ...d, visitDateTime: today() })); }, []);

  const set = useCallback((field, val) => setData(d => ({ ...d, [field]: val })), []);

  const next = () => { window.scrollTo({ top: 0, behavior: "smooth" }); setSection(s => Math.min(s + 1, SECTION_COUNT)); };
  const prev = () => { window.scrollTo({ top: 0, behavior: "smooth" }); setSection(s => Math.max(s - 1, 1)); };
  const goTo = (n) => { window.scrollTo({ top: 0, behavior: "smooth" }); setSection(n); };

  const handleSubmit = async () => {
    setSubmitting(true);
    setResult(null);

    const missingFields = [];
    if (!data.participantId?.trim()) missingFields.push("Participant ID");
    if (!data.visitDateTime?.trim()) missingFields.push("Date and Time");
    if (missingFields.length) {
      setResult({
        type: "error",
        message: `Please complete required field${missingFields.length === 1 ? "" : "s"}: ${missingFields.join(", ")}.`,
      });
      setSubmitting(false);
      return;
    }

    try {
      const response = await submitSurvey(data);
      if (!response?.id) {
        throw new Error("Survey was saved but no submission id was returned.");
      }
      navigate(`/survey/results/${response.id}`);
    } catch (err) {
      setResult({ type: "error", message: "Submission failed. Please try again. " + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  /* ───── symptom block helper ───── */
  function symBlock(title, items) {
    return (
      <div className="symptom-group">
        <h4 className="symptom-group-title">{title}</h4>
        <div className="scale-legend-row">
          <div className="scale-legend">
            <strong>Severity:</strong> {severity.map((s,i) => <span key={i}>{i} = {s}</span>)}
          </div>
          <div className="scale-legend">
            <strong>Frequency:</strong> {frequency.map((f,i) => <span key={i}>{i} = {f}</span>)}
          </div>
        </div>
        {items.map(([label, key]) => (
          <ScaleRow
            key={key} label={label}
            sevValue={data[`sym_${key}_sev`]} freqValue={data[`sym_${key}_freq`]}
            onSev={v => set(`sym_${key}_sev`, v)} onFreq={v => set(`sym_${key}_freq`, v)}
          />
        ))}
      </div>
    );
  }

  /* ───── Render ───── */
  return (
    <div className="page-survey">
      {/* Survey Header */}
      <section className="survey-hero">
        <div className="container">
          <h1>Neuroimmune &amp; Post-Infectious Conditions Survey</h1>
          <p>Estimated time: ~30 minutes &middot; All responses are confidential and protected</p>
          <p className="survey-purpose">To understand health, lifestyle, symptoms, and function among people with complex chronic, post-infectious, and neuroimmune conditions.</p>
        </div>
      </section>

      <div className="survey-layout container">
        {/* Progress sidebar */}
        <aside className="survey-sidebar">
          <div className="survey-progress-card">
            <div className="progress-bar-vertical">
              {Array.from({ length: SECTION_COUNT }, (_, i) => i + 1).map(n => (
                <button key={n} className={`progress-step${section === n ? " active" : ""}${section > n ? " done" : ""}`} onClick={() => goTo(n)}>
                  <span className="step-dot">{section > n ? "✓" : n}</span>
                  <span className="step-label">{["Visit Info","Demographics","Medical History","Quality of Life","Lifestyle","Symptoms","Daily Function","Disease Modules","Treatments"][n-1]}</span>
                </button>
              ))}
            </div>
            <div className="progress-pct">
              <div className="progress-pct-bar"><div className="progress-pct-fill" style={{ width: `${Math.round(((section - 1) / SECTION_COUNT) * 100)}%` }}></div></div>
              <span>{Math.round(((section - 1) / SECTION_COUNT) * 100)}% complete</span>
            </div>
          </div>
        </aside>

        {/* Form body */}
        <main className="survey-body">
          {result && (
            <div className={`form-alert ${result.type === "success" ? "alert-success" : "alert-error"}`}>
              {result.message}
            </div>
          )}

          {/* ──── SECTION 1: Visit Information ──── */}
          {section === 1 && (
            <div className="survey-section fade-in">
              <div className="survey-section-head">
                <span className="section-num">1</span>
                <div><h2>Visit Information</h2><p>Basic information about this visit</p></div>
              </div>
              <div className="survey-fields">
                <Field label="Visit Type" required>
                  <RadioGroup name="visitType" options={["Baseline","Follow-up 1","Follow-up 2"]} value={data.visitType} onChange={v => set("visitType", v)} />
                </Field>
                <Field label="Date and Time (auto-captured)" required>
                  <input type="datetime-local" value={data.visitDateTime} onChange={e => set("visitDateTime", e.target.value)} />
                </Field>
                <Field label="Participant ID" required>
                  <input value={data.participantId} onChange={e => set("participantId", e.target.value)} placeholder="Enter participant identifier" />
                </Field>
                <Field label="Were you fasting today?" required>
                  <RadioGroup name="fasting" options={["Yes","No"]} value={data.fasting} onChange={v => set("fasting", v)} />
                </Field>
                {data.fasting === "Yes" && (
                  <Field label="Hours fasted (0–24)">
                    <input type="number" min="0" max="24" value={data.hoursFasted} onChange={e => set("hoursFasted", e.target.value)} placeholder="0" />
                  </Field>
                )}
                <Field label="Hours since last moderate/vigorous exercise (0–72)">
                  <input type="number" min="0" max="72" value={data.hoursSinceExercise} onChange={e => set("hoursSinceExercise", e.target.value)} placeholder="0" />
                </Field>
                <Field label="Any infection in past 30 days?">
                  <RadioGroup name="infectionPast30" options={["Yes","No"]} value={data.infectionPast30} onChange={v => set("infectionPast30", v)} />
                </Field>
                {data.infectionPast30 === "Yes" && (
                  <div className="conditional-fields">
                    <Field label="Pathogen"><input value={data.infPathogen} onChange={e => set("infPathogen", e.target.value)} /></Field>
                    <Field label="Date"><input type="date" value={data.infDate} onChange={e => set("infDate", e.target.value)} /></Field>
                    <Field label="Care level"><input value={data.infCare} onChange={e => set("infCare", e.target.value)} placeholder="e.g. Home, Urgent care, ER" /></Field>
                  </div>
                )}
                <Field label="Any vaccination in past 90 days?">
                  <RadioGroup name="vaccinePast90" options={["Yes","No"]} value={data.vaccinePast90} onChange={v => set("vaccinePast90", v)} />
                </Field>
                {data.vaccinePast90 === "Yes" && (
                  <div className="conditional-fields">
                    <Field label="Vaccine type"><input value={data.vacType} onChange={e => set("vacType", e.target.value)} /></Field>
                    <Field label="Date"><input type="date" value={data.vacDate} onChange={e => set("vacDate", e.target.value)} /></Field>
                  </div>
                )}
                <Field label="Menstrual status">
                  <RadioGroup name="menstrualStatus" options={["N/A","Cycling","Menopausal","Other"]} value={data.menstrualStatus} onChange={v => set("menstrualStatus", v)} />
                </Field>
                {data.menstrualStatus === "Cycling" && (
                  <Field label="Day of cycle (1–40)">
                    <input type="number" min="1" max="40" value={data.cycleDay} onChange={e => set("cycleDay", e.target.value)} />
                  </Field>
                )}
              </div>
            </div>
          )}

          {/* ──── SECTION 2: Demographics ──── */}
          {section === 2 && (
            <div className="survey-section fade-in">
              <div className="survey-section-head">
                <span className="section-num">2</span>
                <div><h2>Demographics</h2><p>Background information</p></div>
              </div>
              <div className="survey-fields">
                <Field label="Year of birth (YYYY)" required>
                  <input type="number" min="1900" max="2026" value={data.birthYear} onChange={e => set("birthYear", e.target.value)} placeholder="YYYY" />
                </Field>
                <Field label="Sex assigned at birth" required>
                  <RadioGroup name="sexAtBirth" options={["Female","Male","Intersex","Prefer not to say"]} value={data.sexAtBirth} onChange={v => set("sexAtBirth", v)} />
                </Field>
                <Field label="Gender identity">
                  <RadioGroup name="genderIdentity" options={["Woman","Man","Nonbinary","Other"]} value={data.genderIdentity} onChange={v => set("genderIdentity", v)} />
                </Field>
                <Field label="Race / Ethnicity (select all that apply)">
                  <CheckboxGroup options={["Indigenous American / Alaska Native","South Asian","East Asian","African / African American / Afro Caribbean","Hispanic / Latino","Middle Eastern / North African","Pacific Islander","White / European"]} value={data.raceEthnicity} onChange={v => set("raceEthnicity", v)} />
                </Field>
                <div className="field-row-3">
                  <Field label="Country of residence"><input value={data.country} onChange={e => set("country", e.target.value)} /></Field>
                  <Field label="State / Province"><input value={data.state} onChange={e => set("state", e.target.value)} /></Field>
                  <Field label="Postal Code"><input value={data.postalCode} onChange={e => set("postalCode", e.target.value)} /></Field>
                </div>
                <Field label="Marital status">
                  <RadioGroup name="maritalStatus" options={["Single","Married / Partnered","Divorced","Widowed","Prefer not to say"]} value={data.maritalStatus} onChange={v => set("maritalStatus", v)} />
                </Field>
                <Field label="Children in home">
                  <RadioGroup name="childrenHome" options={["Yes","No"]} value={data.childrenHome} onChange={v => set("childrenHome", v)} />
                </Field>
                {data.childrenHome === "Yes" && (
                  <Field label="Number of children under 18">
                    <input type="number" min="0" value={data.childrenCount} onChange={e => set("childrenCount", e.target.value)} />
                  </Field>
                )}
                <Field label="Highest education">
                  <RadioGroup name="education" options={["Less than HS","HS","Some college","College","Graduate degree"]} value={data.education} onChange={v => set("education", v)} />
                </Field>
                <Field label="Employment">
                  <RadioGroup name="employment" options={["Full-time","Part-time","Self-employed","Student","Homemaker","Disabled","Retired","Unemployed"]} value={data.employment} onChange={v => set("employment", v)} />
                </Field>
                <Field label="Annual household income (USD, before tax)">
                  <RadioGroup name="income" options={["< $30k","$30–65k","$65–100k","> $100k","Prefer not to say"]} value={data.income} onChange={v => set("income", v)} />
                </Field>
                <Field label="Military service">
                  <RadioGroup name="militaryService" options={["Yes","No"]} value={data.militaryService} onChange={v => set("militaryService", v)} />
                </Field>
                <Field label="Current tobacco / vape use">
                  <RadioGroup name="tobaccoUse" options={["Never","Former","Current"]} value={data.tobaccoUse} onChange={v => set("tobaccoUse", v)} />
                </Field>
              </div>
            </div>
          )}

          {/* ──── SECTION 3: Medical History & Diagnoses ──── */}
          {section === 3 && (
            <div className="survey-section fade-in">
              <div className="survey-section-head">
                <span className="section-num">3</span>
                <div><h2>Medical History &amp; Diagnoses</h2><p>Your health history and current conditions</p></div>
              </div>
              <div className="survey-fields">
                <Field label="When did your current illness begin? (MM/YYYY)">
                  <input type="month" value={data.illnessOnset} onChange={e => set("illnessOnset", e.target.value)} />
                </Field>
                <Field label="Possible trigger(s) (select all that apply)">
                  <CheckboxGroup options={["Infection","Surgery","Vaccination","Trauma","Pregnancy","Gradual","Other"]} value={data.triggers} onChange={v => set("triggers", v)} />
                </Field>
                <Field label="Diagnoses ever received (select all that apply)">
                  <CheckboxGroup options={["Long COVID","ME/CFS","POTS","MCAS","EDS","Sjögren's","Migraine","IBS/IBD","Asthma","Allergy","Other"]} value={data.diagnoses} onChange={v => set("diagnoses", v)} />
                </Field>
                <Field label="Most impactful condition">
                  <input value={data.mostImpactful} onChange={e => set("mostImpactful", e.target.value)} placeholder="Describe the condition that affects you most" />
                </Field>
                <div className="field-row-2">
                  <Field label="Number of COVID-19 infections"><input type="number" min="0" value={data.covidCount} onChange={e => set("covidCount", e.target.value)} /></Field>
                  <Field label="Dates"><input value={data.covidDates} onChange={e => set("covidDates", e.target.value)} placeholder="e.g. Jan 2022, Mar 2023" /></Field>
                </div>
                <div className="field-row-2">
                  <Field label="COVID severity"><input value={data.covidSeverity} onChange={e => set("covidSeverity", e.target.value)} placeholder="e.g. Mild, Moderate, Severe" /></Field>
                  <Field label="COVID treatment"><input value={data.covidTreatment} onChange={e => set("covidTreatment", e.target.value)} placeholder="e.g. Paxlovid, None" /></Field>
                </div>
                <div className="field-row-2">
                  <Field label="Hospital / ER visits (past 12 months)"><input type="number" min="0" value={data.erVisits12mo} onChange={e => set("erVisits12mo", e.target.value)} /></Field>
                  <Field label="Reason(s)"><input value={data.erReasons} onChange={e => set("erReasons", e.target.value)} /></Field>
                </div>
                <Field label="Family history of similar illness">
                  <RadioGroup name="familyHistory" options={["Yes","No"]} value={data.familyHistory} onChange={v => set("familyHistory", v)} />
                </Field>
                {data.familyHistory === "Yes" && (
                  <Field label="Describe family history">
                    <textarea value={data.familyDesc} onChange={e => set("familyDesc", e.target.value)} rows={3} />
                  </Field>
                )}
                <Field label="Known allergies / intolerances">
                  <textarea value={data.allergies} onChange={e => set("allergies", e.target.value)} rows={2} placeholder="List any allergies or intolerances" />
                </Field>
              </div>
            </div>
          )}

          {/* ──── SECTION 4: General Health & Quality of Life ──── */}
          {section === 4 && (
            <div className="survey-section fade-in">
              <div className="survey-section-head">
                <span className="section-num">4</span>
                <div><h2>General Health &amp; Quality of Life</h2><p>Your overall health and well-being</p></div>
              </div>
              <div className="survey-fields">
                <Field label="Overall health" required>
                  <RadioGroup name="overallHealth" options={["Excellent","Very good","Good","Fair","Poor"]} value={data.overallHealth} onChange={v => set("overallHealth", v)} />
                </Field>
                <Field label="Change vs one year ago">
                  <RadioGroup name="healthChange" options={["Much better","Somewhat better","About the same","Somewhat worse","Much worse"]} value={data.healthChange} onChange={v => set("healthChange", v)} />
                </Field>
                <div className="field-row-2">
                  <Field label="Average pain last 7 days (0–10)">
                    <input type="number" min="0" max="10" value={data.avgPain7d} onChange={e => set("avgPain7d", e.target.value)} />
                  </Field>
                  <Field label="Worst pain in 30 days (0–10)">
                    <input type="number" min="0" max="10" value={data.worstPain30d} onChange={e => set("worstPain30d", e.target.value)} />
                  </Field>
                </div>
                <Field label="Fatigue severity">
                  <RadioGroup name="fatigueSeverity" options={["0 None","1 Mild","2 Moderate","3 Severe","4 Very Severe"]} value={data.fatigueSeverity} onChange={v => set("fatigueSeverity", v)} />
                </Field>
                <Field label="Post-exertional malaise experienced?">
                  <RadioGroup name="pem" options={["Yes","No"]} value={data.pem} onChange={v => set("pem", v)} />
                </Field>
                {data.pem === "Yes" && (
                  <div className="conditional-fields">
                    <Field label="Onset delay"><input value={data.pemOnset} onChange={e => set("pemOnset", e.target.value)} placeholder="e.g. 12 hours" /></Field>
                    <Field label="Duration"><input value={data.pemDuration} onChange={e => set("pemDuration", e.target.value)} placeholder="e.g. 2 days" /></Field>
                  </div>
                )}
                <Field label="Sleep duration (hours/night)">
                  <input type="number" min="0" max="24" step="0.5" value={data.sleepHours} onChange={e => set("sleepHours", e.target.value)} />
                </Field>
                <Field label="Unrefreshing sleep (0–4)">
                  <RadioGroup name="unrefreshingSleep" options={["0","1","2","3","4"]} value={data.unrefreshingSleep} onChange={v => set("unrefreshingSleep", v)} />
                </Field>
                <Field label="Insomnia (0–4)">
                  <RadioGroup name="insomnia" options={["0","1","2","3","4"]} value={data.insomnia} onChange={v => set("insomnia", v)} />
                </Field>
                <div className="field-row-2">
                  <Field label="Anxiety (0–4)">
                    <RadioGroup name="anxiety" options={["0","1","2","3","4"]} value={data.anxiety} onChange={v => set("anxiety", v)} />
                  </Field>
                  <Field label="Low mood (0–4)">
                    <RadioGroup name="lowMood" options={["0","1","2","3","4"]} value={data.lowMood} onChange={v => set("lowMood", v)} />
                  </Field>
                </div>
                <Field label="Social participation limitations (0–4)">
                  <RadioGroup name="socialLimit" options={["0","1","2","3","4"]} value={data.socialLimit} onChange={v => set("socialLimit", v)} />
                </Field>
                <Field label="Work / school impact">
                  <RadioGroup name="workImpact" options={["No change","Reduced hours","Stopped"]} value={data.workImpact} onChange={v => set("workImpact", v)} />
                </Field>
              </div>
            </div>
          )}

          {/* ──── SECTION 5: Lifestyle, Sleep & Environment ──── */}
          {section === 5 && (
            <div className="survey-section fade-in">
              <div className="survey-section-head">
                <span className="section-num">5</span>
                <div><h2>Lifestyle, Sleep &amp; Environment</h2><p>Your daily habits and environment</p></div>
              </div>
              <div className="survey-fields">
                <Field label="Alcohol use">
                  <RadioGroup name="alcohol" options={["None","Occasional","Weekly","Daily"]} value={data.alcohol} onChange={v => set("alcohol", v)} />
                </Field>
                <Field label="Caffeine per day">
                  <RadioGroup name="caffeine" options={["0","1–2","3–4","5+"]} value={data.caffeine} onChange={v => set("caffeine", v)} />
                </Field>
                <Field label="Diet pattern(s) (select all)">
                  <CheckboxGroup options={["Standard","Low FODMAP","Low histamine","Gluten-free","Other"]} value={data.dietPatterns} onChange={v => set("dietPatterns", v)} />
                </Field>
                <Field label="Supplements used">
                  <RadioGroup name="supplements" options={["Yes","No"]} value={data.supplements} onChange={v => set("supplements", v)} />
                </Field>
                {data.supplements === "Yes" && (
                  <Field label="List supplements">
                    <textarea value={data.supplementList} onChange={e => set("supplementList", e.target.value)} rows={2} placeholder="e.g. Vitamin D, CoQ10, Magnesium" />
                  </Field>
                )}
                <div className="field-row-3">
                  <Field label="Light activity (min/week)"><input type="number" min="0" value={data.lightMin} onChange={e => set("lightMin", e.target.value)} /></Field>
                  <Field label="Moderate activity (min/week)"><input type="number" min="0" value={data.moderateMin} onChange={e => set("moderateMin", e.target.value)} /></Field>
                  <Field label="Vigorous activity (min/week)"><input type="number" min="0" value={data.vigorousMin} onChange={e => set("vigorousMin", e.target.value)} /></Field>
                </div>
                <Field label="Use pacing strategies">
                  <RadioGroup name="pacing" options={["Yes","No"]} value={data.pacing} onChange={v => set("pacing", v)} />
                </Field>
                <div className="field-row-3">
                  <Field label="Typical bedtime"><input type="time" value={data.bedtime} onChange={e => set("bedtime", e.target.value)} /></Field>
                  <Field label="Wake time"><input type="time" value={data.wakeTime} onChange={e => set("wakeTime", e.target.value)} /></Field>
                  <Field label="Naps per week"><input type="number" min="0" value={data.napsPerWeek} onChange={e => set("napsPerWeek", e.target.value)} /></Field>
                </div>
                <Field label="Perceived stress (0 None – 10 Extreme)">
                  <input type="range" min="0" max="10" value={data.perceivedStress || 0} onChange={e => set("perceivedStress", e.target.value)} className="range-input" />
                  <div className="range-labels"><span>0 – None</span><span>{data.perceivedStress || 0}</span><span>10 – Extreme</span></div>
                </Field>
                <div className="field-row-2">
                  <Field label="Mold exposure (0–4)">
                    <RadioGroup name="moldExposure" options={["0","1","2","3","4"]} value={data.moldExposure} onChange={v => set("moldExposure", v)} />
                  </Field>
                  <Field label="Chemical sensitivity (0–4)">
                    <RadioGroup name="chemSensitivity" options={["0","1","2","3","4"]} value={data.chemSensitivity} onChange={v => set("chemSensitivity", v)} />
                  </Field>
                </div>
                <div className="field-row-3">
                  <Field label="Pets at home"><RadioGroup name="petsHome" options={["Yes","No"]} value={data.petsHome} onChange={v => set("petsHome", v)} /></Field>
                  <Field label="Travel past 3 months"><RadioGroup name="travelPast3mo" options={["Yes","No"]} value={data.travelPast3mo} onChange={v => set("travelPast3mo", v)} /></Field>
                  <Field label="Altitude exposure"><RadioGroup name="altitudeExposure" options={["Yes","No"]} value={data.altitudeExposure} onChange={v => set("altitudeExposure", v)} /></Field>
                </div>
              </div>
            </div>
          )}

          {/* ──── SECTION 6: Symptom Inventory ──── */}
          {section === 6 && (
            <div className="survey-section fade-in">
              <div className="survey-section-head">
                <span className="section-num">6</span>
                <div><h2>Symptom Inventory</h2><p>Rate each symptom for severity &amp; frequency over the past 6 months</p></div>
              </div>
              <div className="survey-fields">
                {symBlock("Autonomic / Cardiopulmonary", [
                  ["Dizziness","dizziness"],["Palpitations","palpitations"],["Chest discomfort","chest"],
                  ["Shortness of breath","sob"],["Temperature regulation","tempReg"],["Sweating","sweating"],
                ])}
                {symBlock("Neurologic / Cognitive", [
                  ["Brain fog","brainFog"],["Memory issues","memory"],["Headache / Migraine","headache"],
                  ["Sleepiness","sleepiness"],["Insomnia","insomnia"],["Light sensitivity","lightSens"],["Tinnitus","tinnitus"],
                ])}
                {symBlock("Pain / Musculoskeletal", [
                  ["Widespread pain","widePain"],["Joint pain","jointPain"],["Instability","instability"],
                  ["Weakness","weakness"],["Stiffness","stiffness"],["Spasms","spasms"],
                ])}
                {symBlock("Gastrointestinal / Urinary", [
                  ["Nausea","nausea"],["Abdominal pain","abPain"],["Bloating","bloating"],
                  ["Diarrhea","diarrhea"],["Constipation","constipation"],["Urinary urgency/frequency","urinary"],["Incomplete emptying","incomplete"],
                ])}
                {symBlock("Immune / Allergy / Skin", [
                  ["Flushing","flushing"],["Hives","hives"],["Food intolerance","foodIntol"],
                  ["Heat/pressure reaction","heatPressure"],["Dry eyes/mouth","dryEyes"],["Dental issues","dental"],
                ])}
                {symBlock("Mood / Energy", [
                  ["Low mood","lowMood"],["Anxiety","anxietySym"],["Irritability","irritability"],
                  ["Unrefreshing sleep","unrefSleep"],["Night sweats","nightSweats"],["Fatigue persistence","fatiguePersist"],
                ])}
              </div>
            </div>
          )}

          {/* ──── SECTION 7: Daily Function & Activity ──── */}
          {section === 7 && (
            <div className="survey-section fade-in">
              <div className="survey-section-head">
                <span className="section-num">7</span>
                <div><h2>Daily Function &amp; Activity</h2><p>Your daily activity levels and functional capacity</p></div>
              </div>
              <div className="survey-fields">
                <div className="field-row-2">
                  <Field label="Average hours upright daily (0–16)">
                    <input type="number" min="0" max="16" value={data.hoursUpright} onChange={e => set("hoursUpright", e.target.value)} />
                  </Field>
                  <Field label="Hours lying down / bedbound daily (0–24)">
                    <input type="number" min="0" max="24" value={data.hoursLying} onChange={e => set("hoursLying", e.target.value)} />
                  </Field>
                </div>
                <Field label="Mobility aids">
                  <RadioGroup name="mobilityAids" options={["None","Cane","Walker","Wheelchair"]} value={data.mobilityAids} onChange={v => set("mobilityAids", v)} />
                </Field>
                <Field label="Work or school now">
                  <RadioGroup name="workNow" options={["Yes","No","Modified"]} value={data.workNow} onChange={v => set("workNow", v)} />
                </Field>
                <Field label="Work/School days missed (past 4 weeks)">
                  <input type="number" min="0" value={data.daysMissed} onChange={e => set("daysMissed", e.target.value)} />
                </Field>
                <Field label="Difficulty with personal activities (ADLs) (0–4)">
                  <RadioGroup name="adlDifficulty" options={["0","1","2","3","4"]} value={data.adlDifficulty} onChange={v => set("adlDifficulty", v)} />
                </Field>
                <Field label="Difficulty with household/instrumental activities (IADLs) (0–4)">
                  <RadioGroup name="iadlDifficulty" options={["0","1","2","3","4"]} value={data.iadlDifficulty} onChange={v => set("iadlDifficulty", v)} />
                </Field>
                <Field label="Social participation limit (0–4)">
                  <RadioGroup name="socialParticipation" options={["0","1","2","3","4"]} value={data.socialParticipation} onChange={v => set("socialParticipation", v)} />
                </Field>
                <Field label="Caregiver hours per week">
                  <input type="number" min="0" value={data.caregiverHours} onChange={e => set("caregiverHours", e.target.value)} />
                </Field>
              </div>
            </div>
          )}

          {/* ──── SECTION 8: Disease-Specific Modules ──── */}
          {section === 8 && (
            <div className="survey-section fade-in">
              <div className="survey-section-head">
                <span className="section-num">8</span>
                <div><h2>Disease-Specific Modules</h2><p>Answer only the sections that apply to your diagnoses</p></div>
              </div>
              <div className="survey-fields">
                {/* Long COVID */}
                <div className="disease-module">
                  <h3 className="module-title">Long COVID</h3>
                  <div className="field-row-2">
                    <Field label="Infection dates"><input value={data.lc_infectionDates} onChange={e => set("lc_infectionDates", e.target.value)} placeholder="e.g. Jan 2022, Dec 2023" /></Field>
                    <Field label="Reinfections (count)"><input type="number" min="0" value={data.lc_reinfectionCount} onChange={e => set("lc_reinfectionCount", e.target.value)} /></Field>
                  </div>
                  <Field label="Reinfection dates"><input value={data.lc_reinfectionDates} onChange={e => set("lc_reinfectionDates", e.target.value)} /></Field>
                  <Field label="Vaccination before first infection?"><RadioGroup name="lc_vacBeforeFirst" options={["Yes","No"]} value={data.lc_vacBeforeFirst} onChange={v => set("lc_vacBeforeFirst", v)} /></Field>
                  <Field label="Current symptoms"><textarea value={data.lc_currentSymptoms} onChange={e => set("lc_currentSymptoms", e.target.value)} rows={2} /></Field>
                  <div className="field-row-2">
                    <Field label="Hospitalization (count)"><input type="number" min="0" value={data.lc_hospCount} onChange={e => set("lc_hospCount", e.target.value)} /></Field>
                    <Field label="Hospitalization dates"><input value={data.lc_hospDates} onChange={e => set("lc_hospDates", e.target.value)} /></Field>
                  </div>
                  <Field label="Change after vaccine?"><RadioGroup name="lc_changeAfterVac" options={["Yes","No"]} value={data.lc_changeAfterVac} onChange={v => set("lc_changeAfterVac", v)} /></Field>
                </div>

                {/* ME/CFS */}
                <div className="disease-module">
                  <h3 className="module-title">ME/CFS</h3>
                  <Field label="Severe fatigue not relieved by rest?"><RadioGroup name="mecfs_severeFatigue" options={["Yes","No"]} value={data.mecfs_severeFatigue} onChange={v => set("mecfs_severeFatigue", v)} /></Field>
                  <Field label="Infection onset"><input value={data.mecfs_infectionOnset} onChange={e => set("mecfs_infectionOnset", e.target.value)} placeholder="Describe onset event" /></Field>
                  <Field label="Limitation level"><input value={data.mecfs_limitation} onChange={e => set("mecfs_limitation", e.target.value)} placeholder="e.g. Mild, Moderate, Severe" /></Field>
                  <Field label="Housebound frequency"><input value={data.mecfs_housebound} onChange={e => set("mecfs_housebound", e.target.value)} placeholder="e.g. Most days, Some days" /></Field>
                </div>

                {/* POTS/Dysautonomia */}
                <div className="disease-module">
                  <h3 className="module-title">POTS / Dysautonomia</h3>
                  <Field label="HR rise >30 bpm on standing?"><RadioGroup name="pots_hrRise" options={["Yes","No"]} value={data.pots_hrRise} onChange={v => set("pots_hrRise", v)} /></Field>
                  <Field label="Fluid intake (L/day)"><input type="number" min="0" step="0.5" value={data.pots_fluidIntake} onChange={e => set("pots_fluidIntake", e.target.value)} /></Field>
                  <Field label="Salt use"><RadioGroup name="pots_saltUse" options={["None","Low","Moderate","High"]} value={data.pots_saltUse} onChange={v => set("pots_saltUse", v)} /></Field>
                  <Field label="Compression garments"><RadioGroup name="pots_compression" options={["Yes","No"]} value={data.pots_compression} onChange={v => set("pots_compression", v)} /></Field>
                  {data.pots_compression === "Yes" && (
                    <Field label="Hours/day"><input type="number" min="0" max="24" value={data.pots_compressionHrs} onChange={e => set("pots_compressionHrs", e.target.value)} /></Field>
                  )}
                  <Field label="Upright exercise intolerance (0 very tolerant – 4 cannot tolerate)">
                    <RadioGroup name="pots_uprightIntol" options={["0","1","2","3","4"]} value={data.pots_uprightIntol} onChange={v => set("pots_uprightIntol", v)} />
                  </Field>
                </div>

                {/* MCAS */}
                <div className="disease-module">
                  <h3 className="module-title">MCAS</h3>
                  <Field label="Triggers (food, heat, stress, etc.)"><textarea value={data.mcas_triggers} onChange={e => set("mcas_triggers", e.target.value)} rows={2} /></Field>
                  <Field label="Epinephrine carriage"><RadioGroup name="mcas_epiCarriage" options={["Yes","No"]} value={data.mcas_epiCarriage} onChange={v => set("mcas_epiCarriage", v)} /></Field>
                  <Field label="Flushing severity"><RadioGroup name="mcas_flushing" options={["0 None","1 Mild","2 Moderate","3 Severe","4 Very Severe"]} value={data.mcas_flushing} onChange={v => set("mcas_flushing", v)} /></Field>
                  <Field label="Anaphylactoid episodes in past 12 months"><input type="number" min="0" value={data.mcas_anaphylactoid} onChange={e => set("mcas_anaphylactoid", e.target.value)} /></Field>
                </div>

                {/* Sjögren's */}
                <div className="disease-module">
                  <h3 className="module-title">Sjögren's</h3>
                  <Field label="Dry eyes (≥ 3x/day)"><RadioGroup name="sjogren_dryEyes" options={["Yes","No"]} value={data.sjogren_dryEyes} onChange={v => set("sjogren_dryEyes", v)} /></Field>
                  <Field label="Dry mouth (frequent fluids required)"><RadioGroup name="sjogren_dryMouth" options={["Yes","No"]} value={data.sjogren_dryMouth} onChange={v => set("sjogren_dryMouth", v)} /></Field>
                  <Field label="Dental issues / parotid swelling"><RadioGroup name="sjogren_dental" options={["Yes","No"]} value={data.sjogren_dental} onChange={v => set("sjogren_dental", v)} /></Field>
                </div>

                {/* EDS / Hypermobility */}
                <div className="disease-module">
                  <h3 className="module-title">EDS / Hypermobility</h3>
                  <Field label="Beighton score (0–9)"><input type="number" min="0" max="9" value={data.eds_beighton} onChange={e => set("eds_beighton", e.target.value)} /></Field>
                  <Field label="Dislocations/subluxations in past 12 months"><input type="number" min="0" value={data.eds_dislocations} onChange={e => set("eds_dislocations", e.target.value)} /></Field>
                  <Field label="Pain impact on function"><RadioGroup name="eds_painImpact" options={["0 None","1 Mild","2 Moderate","3 Severe","4 Very Severe"]} value={data.eds_painImpact} onChange={v => set("eds_painImpact", v)} /></Field>
                  <Field label="Braces / physio use"><RadioGroup name="eds_bracesPhysio" options={["Yes","No"]} value={data.eds_bracesPhysio} onChange={v => set("eds_bracesPhysio", v)} /></Field>
                </div>

                {/* Migraine */}
                <div className="disease-module">
                  <h3 className="module-title">Migraine</h3>
                  <Field label="Headache days (past 3 months, days/week)"><input type="number" min="0" max="7" value={data.migraine_headacheDays} onChange={e => set("migraine_headacheDays", e.target.value)} /></Field>
                  <Field label="Missed work/school (past 3 months, count of days)"><input type="number" min="0" value={data.migraine_missedWork} onChange={e => set("migraine_missedWork", e.target.value)} /></Field>
                  <Field label="Pain (0–10)"><input type="number" min="0" max="10" value={data.migraine_pain} onChange={e => set("migraine_pain", e.target.value)} /></Field>
                  <Field label="Reduced productivity days (past 3 months)"><input type="number" min="0" value={data.migraine_reducedProd} onChange={e => set("migraine_reducedProd", e.target.value)} /></Field>
                </div>
              </div>
            </div>
          )}

          {/* ──── SECTION 9: Treatments & Care Barriers ──── */}
          {section === 9 && (
            <div className="survey-section fade-in">
              <div className="survey-section-head">
                <span className="section-num">9</span>
                <div><h2>Treatments &amp; Care Barriers</h2><p>Current treatments, interventions, and access challenges</p></div>
              </div>
              <div className="survey-fields">
                <Field label="Current prescriptions (name / dose / frequency / start date)">
                  <textarea value={data.prescriptions} onChange={e => set("prescriptions", e.target.value)} rows={4} placeholder="e.g. Metoprolol 25mg daily, started Jan 2024" />
                </Field>
                <Field label="Non-drug interventions & benefit (−2 much worse to +2 much better)">
                  <textarea value={data.nonDrugInterventions} onChange={e => set("nonDrugInterventions", e.target.value)} rows={4} placeholder="e.g. Pacing (+2), PT (+1), Compression (+1), Salt/fluid loading (0)" />
                </Field>
                <Field label="Adverse effects from treatments">
                  <textarea value={data.adverseEffects} onChange={e => set("adverseEffects", e.target.value)} rows={3} />
                </Field>
                <div className="field-row-2">
                  <Field label="ER/urgent care visits (past 30 days)"><input type="number" min="0" value={data.erVisits30d} onChange={e => set("erVisits30d", e.target.value)} /></Field>
                  <Field label="Reason(s)"><input value={data.erReasons30d} onChange={e => set("erReasons30d", e.target.value)} /></Field>
                </div>
                <Field label="Barriers to care (select all that apply)">
                  <CheckboxGroup options={["Access","Cost","Transportation","Clinician knowledge","Other"]} value={data.barriers} onChange={v => set("barriers", v)} />
                </Field>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="survey-nav">
            {section > 1 && (
              <button type="button" className="btn-outline-action" onClick={prev}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                Previous
              </button>
            )}
            <div className="survey-nav-spacer" />
            {section < SECTION_COUNT ? (
              <button type="button" className="btn-primary-action" onClick={next}>
                Next Section
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
            ) : (
              <button type="button" className="btn-primary-action btn-submit-survey" onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Survey"}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
